import React, { useState, useEffect, useCallback } from "react";
import FilterDropdowns from "../../components/FilterDropdowns/FilterDropdowns";
import ExploreCareersTiles from "./ExploreCareersTiles";
import { filterCareersEventBuilder, sendEvent } from "../../utils/event-helpers";
import { fetchResourceTilesByTagFilters, TaggedResourceTiles } from "../../utils/contentful-helpers";
import { useAppDispatch, useAppSelector } from "../../app/store";
import { markRecommendationFiltersAdjusted } from "../../stores/recommendations/mentorRecommendationsSlice";
import { useOutletContext } from "react-router-dom";
import { RecommenderOutletContext } from "../RecommenderPage/RecommenderPage";

const ExploreCareers = () => {
  const [taggedResourceTiles, setTaggedResourceTiles] = useState<TaggedResourceTiles[]>([]);
  const { pageFilters, setPageFilters } = useOutletContext<RecommenderOutletContext>();
  
  const selectedSubfields = pageFilters.exploreCareers.subfields;
    
  const { userId, sessionId } = useAppSelector((state) => state.session);
  const recommendations = useAppSelector((state) => state.mentorRecommendations.data);
  const dispatch = useAppDispatch();

  const updateExploreCareersFilters = useCallback((subfields: string[]) => {
    setPageFilters((prev) => ({
      ...prev,
      exploreCareers: { subfields },
    }));
  }, [setPageFilters]);

  const filteredResourceTiles = taggedResourceTiles.map((group) => {
    if (selectedSubfields.length === 0) {
      return group;
    }

    return {
      ...group,
      data: group.data.filter((tile) => {
        const keywords = tile.data.keyword || [];

        const matchesSubfield =
          selectedSubfields.length > 0
            ? selectedSubfields.some((sub) => keywords.includes(sub))
            : true;

        return matchesSubfield;
      }),
    };
  });
  
  useEffect(() => {
    if (!recommendations) return;
    if (recommendations.recommendedFilters.adjusted) return;

    updateExploreCareersFilters(recommendations.recommendedFilters.subFields)
  }, [recommendations, updateExploreCareersFilters])

  useEffect(() => {
    const tagFilters = ["Career General Resources", "Career Additional Resources"];
    fetchResourceTilesByTagFilters(tagFilters).then((results) => {
      setTaggedResourceTiles(results);
    });
  }, []);

  useEffect(() => {
    const allFilteredTiles = filteredResourceTiles.map(tile => tile.data).flat();
    const allTileTitles = allFilteredTiles.map(tile => tile.title);

    const eventData = filterCareersEventBuilder(userId, sessionId, allTileTitles, {
      subfieldsFilter: selectedSubfields,
    });

    sendEvent(eventData);
  }, [userId, sessionId, selectedSubfields, filteredResourceTiles])

  const handleSubfieldsChange = (subfields: string[]) => {
    dispatch(markRecommendationFiltersAdjusted());
    updateExploreCareersFilters(subfields);
  };

  const handleClearFilters = () => {
    dispatch(markRecommendationFiltersAdjusted());
    updateExploreCareersFilters([]);
  };

  return (
    <>
      <div className="explore-careers">
        <div className="resources-container">
          <div className="align-headers">
            <div>
              <p
                className="career-highlighted-paragraph"
                style={{ paddingBottom: "14px", marginTop: "17px" }}
              >
                Career Exploration Resources
                <span style={{ marginRight: "15.5rem" }}></span>
              </p>
            </div>
            <div className="dropdown-list">
              <FilterDropdowns
                  showSubfield={true}
                  selectedSubfields={selectedSubfields}
                  onSubfieldsChange={handleSubfieldsChange}
                  onClear={handleClearFilters}
              />
            </div>
          </div>
        </div>
        <div className="gap"></div>
        {/* Toggle Section Links for Mobile */}
        <div className="section-links">
          <a href="#general-resources" className="link-item">
            General Resources
          </a>
          <a href="#additional-resources" className="link-item">
            Subfield-Based Resources
          </a>
        </div>
      </div>

      <div>
        {filteredResourceTiles.map(tile => (
          <ExploreCareersTiles
            key={tile.tagFilter}
            resourceTiles={tile.data}
          />
        ))}
      </div>
    </>
  );
};

export default ExploreCareers;
